import React, { useState, useEffect, type MouseEvent } from "react";
import {
  AppBar,
  Typography,
  Box,
  Button,
  Drawer,
  IconButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";
import { Amplify, Auth } from "aws-amplify";
import SideBar from "./Sidebar";
import awsExports from "../aws-exports";
import { AccountCircle } from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useConfig } from "../utils/ConfigContext";
import type { AuthState } from "../TokenContext";
import type { CurrentUser } from "../types/user";

Amplify.configure(awsExports);
Auth.configure({
  authenticationFlowType: process.env.REACT_APP_AUTHTYPE,
});

type HeaderProps = {
  user: CurrentUser;
  setUser: React.Dispatch<React.SetStateAction<CurrentUser | null>>;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState | null>>;
};

function Header({ user, setUser, setAuthState }: HeaderProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { settings } = useConfig();

  const handleMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSidebarOpen = () => {
    setIsSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  const handleEditUser = async () => {
    navigate("/account/list/edit?user-id=" + user.id);
    handleClose();
  };

  const handleSignOut = async () => {
    try {
      await Auth.signOut();
      setUser(null);
      setAuthState({ user: null, state: "signIn" });
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  return (
    <div>
      {user ? (
        <React.Fragment>
          <AppBar
            position="static"
            sx={{
              width: "100vw",
              display: "flex",
              flexDirection: "row",
              position: "fixed",
              alignItems: "center",
              zIndex: 500,
              boxShadow: 3,
            }}
          >
            <Button
              onClick={handleSidebarOpen}
              size="small"
              sx={{
                display: "none",
                alignItems: "center",
                color: "white",
                borderColor: "white",
                border: "1px solid",
                minWidth: "48px",
                marginLeft: "16px",
                "@media screen and (max-width:1200px)": {
                  display: "flex",
                  position: "absolute",
                },
              }}
            >
              <MenuIcon />
            </Button>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                height: 72,
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                災害時情報収集システム
              </Typography>
            </Box>

            <Box
              sx={{
                position: "absolute",
                right: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              {settings.maintenance === true ? (
                <Box
                  sx={{
                    borderRadius: "20px",
                    padding: "8px",
                    height: "fit-content",
                    color: "white",
                    fontWeight: "bold",
                    width: "fit-content",
                    backgroundColor: "plum",
                    "@media screen and (max-width:900px)": {
                      display: "none",
                    },
                  }}
                >
                  メンテナンス
                  <Box
                    component="span"
                    sx={{
                      "@media screen and (max-width:1200px)": {
                        display: "none",
                      },
                    }}
                  >
                    モード
                  </Box>
                </Box>
              ) : (
                <></>
              )}
              {settings.training === true ? (
                <Box
                  sx={{
                    borderRadius: "20px",
                    padding: "8px",
                    height: "fit-content",
                    color: "white",
                    fontWeight: "bold",
                    width: "fit-content",
                    backgroundColor: "cadetblue",
                    "@media screen and (max-width:900px)": {
                      display: "none",
                    },
                  }}
                >
                  訓練
                  <Box
                    component="span"
                    sx={{
                      "@media screen and (max-width:1200px)": {
                        display: "none",
                      },
                    }}
                  >
                    モード
                  </Box>
                </Box>
              ) : (
                <></>
              )}
              {settings.dryrun === true ? (
                <Box
                  sx={{
                    borderRadius: "20px",
                    padding: "8px",
                    height: "fit-content",
                    color: "white",
                    fontWeight: "bold",
                    width: "fit-content",
                    backgroundColor: "indianred",
                    "@media screen and (max-width:900px)": {
                      display: "none",
                    },
                  }}
                >
                  テスト
                  <Box
                    component="span"
                    sx={{
                      "@media screen and (max-width:1200px)": {
                        display: "none",
                      },
                    }}
                  >
                    モード
                  </Box>
                </Box>
              ) : (
                <></>
              )}

              <IconButton
                name="accountIcon"
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                  "& .MuiPaper-root": {
                    marginRight: "8px",
                  },
                  width: "280px",
                }}
              >
                <MenuItem
                  name="organizationMenu"
                  onClick={handleClose}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <BusinessIcon />
                  {user.m_organization.department_name}
                </MenuItem>
                <MenuItem name="userMenu" onClick={handleEditUser}>
                  <PersonIcon />
                  {user.name}
                </MenuItem>
                <MenuItem name="logoutMenu" onClick={handleSignOut}>
                  <ExitToAppIcon />
                  ログアウト
                </MenuItem>
                {(settings.maintenance === true ||
                  settings.training === true ||
                  settings.dryrun === true) && (
                  <MenuItem
                    name="maintenanceMenu"
                    sx={{
                      display: "none",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        display: "flex",
                        flexWrap: "wrap",
                        height: "fit-content",
                      },
                    }}
                  >
                    {settings.maintenance === true ? (
                      <Box
                        sx={{
                          borderRadius: "20px",
                          padding: "4px",
                          height: "fit-content",
                          color: "white",
                          fontWeight: "bold",
                          width: "fit-content",
                          backgroundColor: "violet",
                        }}
                      >
                        メンテナンスモード
                      </Box>
                    ) : (
                      <></>
                    )}
                    {settings.training === true ? (
                      <Box
                        sx={{
                          borderRadius: "20px",
                          padding: "4px",
                          height: "fit-content",
                          color: "white",
                          fontWeight: "bold",
                          width: "fit-content",
                          backgroundColor: "green",
                        }}
                      >
                        訓練モード
                      </Box>
                    ) : (
                      <></>
                    )}
                    {settings.dryrun === true ? (
                      <Box
                        sx={{
                          borderRadius: "20px",
                          padding: "4px",
                          height: "fit-content",
                          color: "white",
                          fontWeight: "bold",
                          width: "fit-content",
                          backgroundColor: "red",
                        }}
                      >
                        テストモード
                      </Box>
                    ) : (
                      <></>
                    )}
                  </MenuItem>
                )}
              </Menu>
            </Box>
          </AppBar>
          <Drawer
            anchor="left"
            open={isSidebarOpen}
            onClose={handleSidebarClose}
          >
            <SideBar />
          </Drawer>
        </React.Fragment>
      ) : (
        <></>
      )}
    </div>
  );
}

export default Header;
